/* import __COLOCATED_TEMPLATE__ from './sitemap-domain-selector.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
// @ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import truncateString from 'embercom/lib/truncate-string';
import { hasFeature } from 'embercom/helpers/has-feature';

// Interface definitions
export interface SitemapPage {
  id: string;
  display_name: string;
  path: string;
  url: string;
  level: number;
  parent_id: string | null;
  children_ids: string[];
}

export interface SitemapDomain {
  domain: string;
  pages: SitemapPage[];
}

export interface SelectedDomain {
  domain: string;
  selectedPages: string[];
  startUrls: string[];
  urlsToExclude: string[];
  includeUrls: string[];
  additionalUrls: string[];
}

interface Args {
  form: any;
  domain: SitemapDomain | null;
  pages: SitemapPage[] | undefined;
  selectedPages?: any[];
  onDomainShow?: (domain: SitemapDomain) => Promise<SitemapDomain | void> | SitemapDomain | void;
  onSelectionChange?: (selection: SelectedDomain) => void;
  isSelectedByDefault?: boolean;
  isNewSource?: boolean;
  hasMultipleSubdomainsAndOneHasMultiplePages?: boolean;
  initialExcludeUrls?: string[];
  initialAdditionalUrls?: string[];
  specificPageToSelect?: SitemapPage;
}

interface PageSelection extends SitemapPage {
  isSelected: boolean;
  isExpanded: boolean;
}

interface DomainSelection extends SitemapDomain {
  isExpanded: boolean;
  isSelected: boolean;
  isLoading?: boolean;
  hasNoPages?: boolean;
}

export default class SitemapDomainSelector extends Component<Args> {
  @tracked selectedPages = new Set<string>();
  @tracked expandedPages = new Set<string>();
  @tracked isDomainSelected = false;
  @tracked expandedDomains = new Set<string>();
  @tracked loadingDomains = new Set<string>();
  @tracked domainsWithNoPages = new Set<string>();
  @service declare intercomEventService: any;
  @service declare appService: $TSFixMe;
  @tracked startUrls = new Set<string>();
  @tracked includeUrls = new Set<string>();
  @tracked excludeUrls = new Set<string>();
  // Keep track of initial additional URLs separately - these are never removed
  private initialAdditionalUrls = new Set<string>();

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    if (this.args.initialExcludeUrls?.length) {
      this.excludeUrls = new Set(this.args.initialExcludeUrls);
    }

    if (this.args.initialAdditionalUrls?.length) {
      this.initialAdditionalUrls = new Set(this.args.initialAdditionalUrls);
    }

    this.initialize();
  }

  private initialize(): void {
    if (!this.args.domain) {
      return;
    }

    if (this.args.onDomainShow) {
      this.fetchPagesAndUpdateDomain(this.args.domain);
    }

    if (this.args.isSelectedByDefault) {
      this.toggleDomainSelection(this.args.domain);
    }

    if (this.args.domain.pages?.length === 0) {
      this.updateDomainPagesStatus(this.args.domain.domain, true);
    }

    if (hasFeature('team-knowledge-foundations-root-url-sitemap-display', this.appService)) {
      if (this.args.specificPageToSelect) {
        this.selectSpecificPage(this.args.specificPageToSelect);
      }
    }
  }

  get domain(): DomainSelection {
    let { domain } = this.args;
    if (!domain) {
      return {
        domain: '',
        pages: [],
        isExpanded: false,
        isSelected: false,
        isLoading: false,
        hasNoPages: true,
      } as DomainSelection;
    }

    return {
      ...domain,
      isExpanded: this.expandedDomains.has(domain.domain),
      isSelected: this.isDomainSelected,
      isLoading: this.loadingDomains.has(domain.domain),
      hasNoPages: this.domainsWithNoPages.has(domain.domain),
    } as DomainSelection;
  }

  get items(): PageSelection[] {
    let allItems = this.domain.pages || [];
    let itemsByParent = this.groupItemsByParent(allItems);
    return this.buildOrderedList(itemsByParent, null, 0);
  }

  private groupItemsByParent(items: SitemapPage[]): Map<string | null, SitemapPage[]> {
    let itemsByParent = new Map<string | null, SitemapPage[]>();

    items.forEach((item) => {
      let parentId = item.parent_id;
      if (!itemsByParent.has(parentId)) {
        itemsByParent.set(parentId, []);
      }
      itemsByParent.get(parentId)?.push(item);
    });

    return itemsByParent;
  }

  private buildOrderedList(
    itemsByParent: Map<string | null, SitemapPage[]>,
    parentId: string | null,
    currentLevel = 0,
  ): PageSelection[] {
    let items = itemsByParent.get(parentId) || [];

    return items.reduce((acc: PageSelection[], item) => {
      let currentItem = item;
      let combinedDisplayName = item.display_name;

      while (currentItem.children_ids?.length === 1) {
        let childId = currentItem.children_ids[0];
        let child = this.findPageById(childId);
        if (!child) {
          break;
        }

        combinedDisplayName = `${combinedDisplayName}${child.display_name}`;
        currentItem = child;
      }

      let itemWithState: PageSelection = {
        ...currentItem,
        display_name: combinedDisplayName,
        level: currentLevel,
        isSelected: this.selectedPages.has(currentItem.id),
        isExpanded: this.expandedPages.has(currentItem.id),
      };

      acc.push(itemWithState);

      if (currentItem.children_ids?.length && this.expandedPages.has(currentItem.id)) {
        acc.push(...this.buildOrderedList(itemsByParent, currentItem.id, currentLevel + 1));
      }

      return acc;
    }, []);
  }

  get selectedDomainsWithPages(): SelectedDomain[] {
    let result: SelectedDomain[] = [];

    if (this.isDomainSelected) {
      result.push(this.createDomainSelectionWhenDomainSelected());
    } else if (this.selectedPages.size > 0) {
      result.push(this.createDomainSelectionForIndividualPages());
    }

    return result;
  }

  private createDomainSelectionWhenDomainSelected(): SelectedDomain {
    let selectedPageUrls = this.getSelectedPageUrls();
    let combinedAdditionalUrls = [
      ...Array.from(this.initialAdditionalUrls),
      ...Array.from(this.startUrls),
    ];

    return {
      domain: this.args.domain?.domain || '',
      selectedPages: selectedPageUrls,
      urlsToExclude: Array.from(this.excludeUrls),
      includeUrls: Array.from(this.includeUrls),
      startUrls: Array.from(this.startUrls),
      additionalUrls: [...new Set(combinedAdditionalUrls)],
    };
  }

  private getSelectedPageUrls(): string[] {
    let pages = this.args.domain?.pages || [];

    if (this.includeUrls.size > 0) {
      return pages.filter((page) => this.selectedPages.has(page.id)).map((page) => page.url);
    } else {
      return pages.filter((page) => !this.isPageExcluded(page)).map((page) => page.url);
    }
  }

  private isPageExcluded(page: SitemapPage): boolean {
    return this.excludeUrls.has(page.url) || this.matchesAnyExcludePattern(page.url);
  }

  private matchesAnyExcludePattern(pageUrl: string): boolean {
    let normalizedPageUrl = this.normalizeUrl(pageUrl);

    return Array.from(this.excludeUrls).some((excludePattern) => {
      return this.matchesExcludePattern(normalizedPageUrl, excludePattern, pageUrl);
    });
  }

  private matchesExcludePattern(
    normalizedPageUrl: string,
    excludePattern: string,
    originalPageUrl: string,
  ): boolean {
    let normalizedPattern = this.normalizeUrl(excludePattern);

    // Direct match
    if (normalizedPageUrl === normalizedPattern) {
      return true;
    }

    // Glob pattern matching for /** patterns
    if (normalizedPattern.endsWith('/**')) {
      let patternBase = normalizedPattern.replace('/**', '');
      return normalizedPageUrl.startsWith(patternBase);
    }

    // Partial match (excludePattern is contained in pageUrl)
    if (normalizedPageUrl.includes(normalizedPattern)) {
      return true;
    }

    // Also check for exact URL match with /** suffix
    if (excludePattern === `${originalPageUrl}/**`) {
      return true;
    }

    return false;
  }

  private normalizeUrl(url: string): string {
    if (!url) {
      return '';
    }

    let normalized = url.replace(/^https?:\/\//, '');

    normalized = normalized.replace(/^www\./, '');
    normalized = normalized.replace(/\/$/, '');

    return normalized.toLowerCase();
  }

  private createDomainSelectionForIndividualPages(): SelectedDomain {
    let pages = this.args.domain?.pages || [];
    let selectedPageUrls = pages
      .filter((page) => this.selectedPages.has(page.id))
      .map((page) => page.url);
    let combinedAdditionalUrls = [
      ...Array.from(this.initialAdditionalUrls),
      ...Array.from(this.startUrls),
    ];

    return {
      domain: this.args.domain?.domain || '',
      selectedPages: selectedPageUrls,
      urlsToExclude: Array.from(this.excludeUrls),
      includeUrls: Array.from(this.includeUrls),
      startUrls: Array.from(this.startUrls),
      additionalUrls: [...new Set(combinedAdditionalUrls)],
    };
  }

  async fetchPagesAndUpdateDomain(domain: SitemapDomain): Promise<void> {
    this.loadingDomains.add(domain.domain);
    this.updateSingleTrackedSet('loadingDomains');

    try {
      await this.args.onDomainShow?.(domain);
    } catch (error) {
      this.loadingDomains.delete(domain.domain);
      this.updateSingleTrackedSet('loadingDomains');
    }

    let hasPages = this.domain.pages?.length > 0;
    this.updateDomainPagesStatus(domain.domain, !hasPages);

    this.loadingDomains.delete(domain.domain);
    this.updateSingleTrackedSet('loadingDomains');
  }

  @action
  togglePageExpand(item: PageSelection): void {
    this.toggleSet(this.expandedPages, item.id);
    this.updateSingleTrackedSet('expandedPages');
    this.trackAnalyticsEvent('expanded_page', item.url);
  }

  @action
  toggleDomainExpand(domain: DomainSelection): void {
    if (!domain.hasNoPages) {
      this.toggleSet(this.expandedDomains, domain.domain);
      this.updateSingleTrackedSet('expandedDomains');
    }
    this.trackAnalyticsEvent('expanded_domain', domain.domain);
  }

  private updateDomainPagesStatus(domainKey: string, hasNoPages: boolean): void {
    if (hasNoPages) {
      this.domainsWithNoPages.add(domainKey);
    } else {
      this.domainsWithNoPages.delete(domainKey);
    }
    this.updateSingleTrackedSet('domainsWithNoPages');
  }

  private toggleSet<T>(set: Set<T>, value: T): Set<T> {
    if (set.has(value)) {
      set.delete(value);
    } else {
      set.add(value);
    }
    return new Set(set);
  }

  private hasSomeSelectedChildren(page: PageSelection): boolean {
    return (
      page.children_ids.length > 0 &&
      this.anyPageChildSelected(page) &&
      !this.allPageChildrenSelected(page)
    );
  }

  @action
  togglePageSelection(page: PageSelection): void {
    if (this.selectedPages.has(page.id)) {
      if (this.hasSomeSelectedChildren(page)) {
        this.consolidateChildrenUrlsToParent(page);
        this.selectChildren(page);
        this.removePageFromExcluded(page, true);
        this.updateTrackedSets();
        return;
      }

      this.handlePageDeselection(page);
      this.trackAnalyticsEvent('deselected_page', page.url);
    } else {
      this.handlePageSelection(page);
      this.trackAnalyticsEvent('selected_page', page.url);
    }

    this.updateTrackedSets();
  }

  private handlePageDeselection(page: PageSelection): void {
    let isLastPage = this.isLastSelectedPage(page);

    this.selectedPages.delete(page.id);

    if (page.children_ids?.length) {
      this.unselectChildren(page);
    }

    if (page.parent_id) {
      let parent = this.findPageById(page.parent_id);
      if (parent && this.allPageChildrenDeselected(parent)) {
        this.handleParentDeselection(page);
        this.cleanUpChildrenFromExcluded(page);
        this.updateTrackedSets();
      }
    }

    if (isLastPage || this.selectedPages.size === 0) {
      if (this.args.domain) {
        this.toggleDomainSelection(this.args.domain);
      }
      return;
    }

    this.updateUrlsForDeselection(page);
  }

  private isLastSelectedPage(page: PageSelection): boolean {
    return this.selectedPages.size === 1 && this.selectedPages.has(page.id);
  }

  private cleanUpChildrenFromExcluded(page: PageSelection): void {
    let parent = this.findPageById(page.parent_id ?? '');
    if (parent) {
      this.traverseChildren(parent, (child) => {
        this.removePageFromExcluded(child as PageSelection, false);
      });
    }
  }

  private cleanupChildUrlsFromIncludeAndStart(parent: SitemapPage): void {
    this.traverseChildren(parent, (child) => {
      // Remove child URLs that were added because parent was excluded
      this.includeUrls.delete(child.url);
      this.includeUrls.delete(`${child.url}/**`);
      this.startUrls.delete(child.url);

      if (child.children_ids?.length > 0) {
        this.cleanupChildUrlsFromIncludeAndStart(child);
      }
    });
  }

  private updateUrlsForDeselection(page: PageSelection): void {
    let parentExcluded = this.isParentExplicitlyExcluded(page);

    if (parentExcluded) {
      this.includeUrls.delete(page.url);
      this.includeUrls.delete(`${page.url}/**`);
      this.startUrls.delete(page.url);
    } else {
      this.includeUrls.delete(page.url);
      this.includeUrls.delete(`${page.url}/**`);
      this.startUrls.delete(page.url);

      if (page.children_ids?.length > 0) {
        this.excludeUrls.add(`${page.url}/**`);
      } else {
        this.excludeUrls.add(page.url);
      }
    }
  }

  private handlePageSelection(page: PageSelection): void {
    this.ensureDomainSelected();

    let parentExcluded = this.isParentExplicitlyExcluded(page);

    if (!parentExcluded) {
      this.handleParentSelection(page);
    }

    this.selectedPages.add(page.id);

    if (page.children_ids?.length) {
      this.selectChildren(page);
    }

    this.updateUrls(page);
  }

  private isParentExplicitlyExcluded(page: PageSelection): boolean {
    if (!page.parent_id) {
      return false;
    }

    let parent = this.findPageById(page.parent_id);
    if (!parent) {
      return false;
    }

    // Check if parent is explicitly excluded (not selected and has exclusion pattern)
    return (
      !this.selectedPages.has(parent.id) &&
      (this.excludeUrls.has(parent.url) ||
        this.excludeUrls.has(`${parent.url}/**`) ||
        this.matchesAnyExcludePattern(parent.url))
    );
  }

  private updateUrls(page: PageSelection): void {
    let parentExcluded = this.isParentExplicitlyExcluded(page);

    if (parentExcluded) {
      this.addPageToUrls(page);
    } else {
      if (this.removePageFromExcluded(page)) {
        return;
      }

      this.addPageToUrls(page);

      if (this.allDomainPagesSelected()) {
        this.startUrls.clear();
        this.includeUrls.clear();

        let domainUrl = this.args.domain?.domain || '';
        this.startUrls.add(domainUrl);
        this.includeUrls.add(`${domainUrl}/**`);
      }
    }
  }

  private updateSingleTrackedSet(setName: keyof SitemapDomainSelector): void {
    if (this[setName] instanceof Set) {
      // @ts-ignore - Valid operation but TypeScript can't confirm the type
      this[setName] = new Set(this[setName]);
    }
  }

  private updateTrackedSets(): void {
    let setsToUpdate = [
      'expandedDomains',
      'selectedPages',
      'startUrls',
      'excludeUrls',
      'includeUrls',
    ] as const;

    setsToUpdate.forEach((setName) => this.updateSingleTrackedSet(setName));

    if (this.args.onSelectionChange) {
      if (this.selectedDomainsWithPages.length > 0) {
        this.args.onSelectionChange(this.selectedDomainsWithPages[0]);
      } else {
        this.args.onSelectionChange(undefined as any);
      }
    }
  }

  private clearUrlSets(): void {
    this.excludeUrls.clear();
    this.includeUrls.clear();
    this.startUrls.clear();

    this.updateSingleTrackedSet('excludeUrls');
    this.updateSingleTrackedSet('includeUrls');
    this.updateSingleTrackedSet('startUrls');
  }

  @action
  toggleDomainSelection(domain: SitemapDomain): void {
    let isSelected = this.isDomainSelected;
    let domainValue = this.args.domain?.domain || domain.domain;

    if (isSelected) {
      this.handleDomainDeselection();
      this.trackAnalyticsEvent('deselected_domain', domainValue);
    } else {
      this.handleDomainSelection(domain);
      this.trackAnalyticsEvent('selected_domain', domainValue);
    }
  }

  private handleDomainDeselection(): void {
    this.isDomainSelected = false;
    this.selectedPages.clear();
    this.updateSingleTrackedSet('selectedPages');
    this.clearUrlSets();

    if (this.args.onSelectionChange && this.args.domain) {
      this.args.onSelectionChange({
        domain: this.args.domain.domain,
        selectedPages: [],
        startUrls: [],
        urlsToExclude: [],
        includeUrls: [],
        additionalUrls: Array.from(this.initialAdditionalUrls),
      });
    }
  }

  private handleDomainSelection(domain: SitemapDomain): void {
    this.isDomainSelected = true;
    this.expandedDomains.add(domain.domain);
    this.updateSingleTrackedSet('expandedDomains');

    this.includeUrls.clear();
    this.startUrls.clear();
    this.updateSingleTrackedSet('includeUrls');
    this.updateSingleTrackedSet('startUrls');

    // Select pages, but skip excluded ones
    domain.pages.forEach((page) => {
      if (!this.isPageExcluded(page)) {
        this.selectedPages.add(page.id);
      }
    });

    let domainUrl = domain.domain;
    this.startUrls.add(domainUrl);
    this.includeUrls.add(`${domainUrl}/**`);

    this.updateSingleTrackedSet('selectedPages');
    this.updateSingleTrackedSet('startUrls');
    this.updateSingleTrackedSet('includeUrls');

    if (this.args.onSelectionChange) {
      this.args.onSelectionChange(this.selectedDomainsWithPages[0]);
    }
  }

  private selectSpecificPage(page: SitemapPage): void {
    this.ensureDomainSelected();
    this.selectedPages.add(page.id);
    this.selectChildren(page as PageSelection);
    this.selectAndExpandParents(page);
    this.addPageToUrls(page as PageSelection);
    this.updateTrackedSets();
  }

  private selectAndExpandParents(page: SitemapPage): void {
    if (page.parent_id) {
      let parent = this.findPageById(page.parent_id);
      if (parent) {
        this.selectedPages.add(parent.id);
        this.expandedPages.add(parent.id);

        this.selectAndExpandParents(parent);
      }
    }
  }

  private findPageById(id: string): SitemapPage | undefined {
    let pages = this.domain.pages || [];
    return pages.find((item) => item.id === id);
  }

  private traverseChildren(page: SitemapPage, action: (child: SitemapPage) => void): void {
    page.children_ids.forEach((childId) => {
      let child = this.findPageById(childId);
      if (child) {
        action(child);
        if (child.children_ids?.length) {
          this.traverseChildren(child, action);
        }
      }
    });
  }

  private selectChildren(page: SitemapPage): void {
    this.traverseChildren(page, (child) => {
      this.selectedPages.add(child.id);

      if (this.isDomainSelected) {
        this.excludeUrls.delete(child.url);
      }
    });

    this.updateTrackedSets();
  }

  private unselectChildren(page: SitemapPage): void {
    this.traverseChildren(page, (child) => {
      this.selectedPages.delete(child.id);
    });
  }

  get hasMultipleSubdomainsAndOneHasMultiplePages(): boolean {
    return this.args.hasMultipleSubdomainsAndOneHasMultiplePages ?? false;
  }

  @action
  allDomainPagesSelected(): boolean {
    let pages = this.domain.pages || [];
    return pages.length > 0 && pages.every((page) => this.selectedPages.has(page.id));
  }

  @action
  anyDomainPageSelected(): boolean {
    let pages = this.domain.pages || [];
    return pages.some((page) => this.selectedPages.has(page.id));
  }

  @action
  allPageChildrenSelected(page: SitemapPage): boolean {
    return page.children_ids.every((childId) => this.selectedPages.has(childId));
  }

  @action
  anyPageChildSelected(page: SitemapPage): boolean {
    return page.children_ids.some((childId) => this.selectedPages.has(childId));
  }

  private allPageChildrenDeselected(page: SitemapPage): boolean {
    return page.children_ids.every((childId) => !this.selectedPages.has(childId));
  }

  truncateDisplayName(displayName: string): string {
    return truncateString(displayName, 75);
  }

  getIndentStyle(item: PageSelection): string {
    let baseIndent = 24;
    let indent = (item.level + 1) * baseIndent;
    return sanitizeHtml(`padding-left: ${indent}px`);
  }

  private trackAnalyticsEvent(action: string, object: string, metadata?: any): void {
    this.intercomEventService.trackAnalyticsEvent({
      action,
      object,
      context: 'add_source_modal',
      ...metadata,
    });
  }

  private ensureDomainSelected(): void {
    if (hasFeature('team-knowledge-foundations-root-url-sitemap-display', this.appService)) {
      if (!this.isDomainSelected && this.args.domain) {
        this.isDomainSelected = true;
        this.expandedDomains.add(this.args.domain.domain);
        this.updateSingleTrackedSet('expandedDomains');
      }
    } else if (!this.isDomainSelected) {
      this.isDomainSelected = true;
    }
  }

  private handleParentSelection(page: PageSelection): void {
    let pages = this.domain.pages || [];
    let parent = pages.find((p) => p.id === page.parent_id);
    if (!parent) {
      return;
    }

    if (!this.selectedPages.has(parent.id)) {
      this.selectedPages.add(parent.id);
    }

    if (this.allPageChildrenSelected(parent)) {
      this.cleanupParentExclusion(parent);
    }

    this.removePageFromExcluded(page);

    let parentPage = {
      ...parent,
      isSelected: true,
      isExpanded: true,
    } as PageSelection;

    this.handleParentSelection(parentPage);
  }

  private cleanupParentExclusion(parent: SitemapPage): void {
    let urlsToRemove: string[] = [];

    Array.from(this.excludeUrls).forEach((excludePattern) => {
      if (excludePattern === parent.url || excludePattern === `${parent.url}/**`) {
        urlsToRemove.push(excludePattern);
      }
    });

    urlsToRemove.forEach((url) => {
      this.excludeUrls.delete(url);
    });
  }

  private handleParentDeselection(page: PageSelection): void {
    let pages = this.domain.pages || [];
    let parent = pages.find((p) => p.id === page.parent_id);
    if (!parent) {
      return;
    }

    this.selectedPages.delete(parent.id);
    this.updateUrlsForDeselection(parent as PageSelection);
    this.updateTrackedSets();
  }

  private removePageFromExcluded(page: PageSelection, includeChildren = false): boolean {
    let normalizedPageUrl = this.normalizeUrl(page.url);
    let wasExcluded = false;
    let urlsToRemove: string[] = [];

    Array.from(this.excludeUrls).forEach((excludePattern) => {
      if (this.shouldRemoveExclusionPattern(normalizedPageUrl, excludePattern, page.url)) {
        urlsToRemove.push(excludePattern);
        wasExcluded = true;
      }
    });

    urlsToRemove.forEach((url) => {
      this.excludeUrls.delete(url);
      this.cleanupChildUrlsFromIncludeAndStart(page);
    });

    if (includeChildren) {
      this.traverseChildren(page, (child) => {
        this.removePageFromExcluded(child as PageSelection, true);
      });
    }

    return wasExcluded;
  }

  private shouldRemoveExclusionPattern(
    normalizedPageUrl: string,
    excludePattern: string,
    originalPageUrl: string,
  ): boolean {
    let normalizedPattern = this.normalizeUrl(excludePattern);

    // Some customers manually input raw URL rather than in a glob pattern
    if (normalizedPageUrl === normalizedPattern || originalPageUrl === excludePattern) {
      return true;
    }

    if (excludePattern.endsWith('/**')) {
      let patternBase = excludePattern.replace('/**', '');
      let normalizedPatternBase = this.normalizeUrl(patternBase);
      let normalizedOriginalUrl = this.normalizeUrl(originalPageUrl);

      if (normalizedOriginalUrl === normalizedPatternBase) {
        return true;
      }
    }

    if (excludePattern === `${originalPageUrl}/**`) {
      return true;
    }

    return false;
  }

  private consolidateChildrenUrlsToParent(parentPage: PageSelection): void {
    this.cleanupChildUrlsFromIncludeAndStart(parentPage);

    this.addPageToUrls(parentPage);
  }

  private addPageToUrls(page: PageSelection): void {
    this.startUrls.add(page.url);

    // Add /** if page has children, otherwise add the page URL directly
    if (page.children_ids?.length > 0) {
      this.includeUrls.add(`${page.url}/**`);
    } else {
      this.includeUrls.add(page.url);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'ContentService::AiContentLibrary::Sources::SitemapDomainSelector': typeof SitemapDomainSelector;
  }
}
